import { Injectable } from '@angular/core';
import { OrderItem } from 'com.trumbull.phx.order.api-client';
import { OrderDetails, Plaintiff, RecordSubjectName, State } from '../../orders/order-details/order-details.model';
import { RootObject } from '../../orders/order-details/orders.model';
import { TenantService } from '../../tenant.service';
import _ from 'lodash';
import { IPSTenantKeyService } from '@ips/common-angular';
import { DatePipe } from '@angular/common';
import { DocumentReference } from '../../orders/order-details/record-index-table/record-index-table.component';
import { Defendant, DefendantCounsel, PlaintiffCounsel } from '../../orders/subpoena/subpoena.model';
import { DateUtilService } from '../date-util/date-util.service';

@Injectable({
  providedIn: 'root'
})
export class OrderDetailsMapperService {

  constructor(private datePipe: DatePipe,
    private readonly tenantKeyService: IPSTenantKeyService,
    private readonly dateUtilService: DateUtilService) { }

  vocabData: any;


  mapOrderDetails(order: OrderItem, data: RootObject, vocabData: any, userData: any): OrderDetails {
    let result = new OrderDetails();

    const caseData = data.case;
    const retrievalData = data.retrievalObjective;
    const attachmentsData = data.attachments;
    result.id = order.orderNumber || '';
    result.claimNumber = caseData.referenceNumber ?? 'Unknown';
    result.dateOfIncident = this.formatDate(caseData.incidentDate); // format as mm/dd/yyyy
    result.typeOfOrder = this.getOrderTypeDescription(retrievalData?.orderTypeCode, vocabData) ?? 'Unknown';
    result.serviceLevel = retrievalData?.serviceLevelCode ?? 'Normal';
    result.orderedDate = this.formatDate(order.createdDate);
    result.accountName = this.getAccountNameDescription(order?.orderedByAccountId, vocabData) ?? '';
    result.orderOwner = this.getDisplayName(order?.orderedBy, userData) ?? '';
    result.createdBy = this.getDisplayName(order?.createdBy, userData) ?? '';
    result.clientActionRequired = order.clientActionRequired || false;
    result.orderStatus = this.getOrderStatusDescription(order.statusCode, vocabData) ?? '--';

    result.caseMatter = (caseData as any)?.qualifiers?.caseMatter || '';

    result.subpoena.caseName = caseData?.caseName ?? '';
    result.subpoena.caseNumber = caseData?.caseNumber ?? '';
    
    // set authorization details - case number
    result.caseNumber = caseData?.caseNumber ?? '';
    this.vocabData = vocabData;
    // this.getSelectedTenant(result);
    this.getContactDetails(caseData.parties, result,caseData);
    this.getRetrievalDetails(retrievalData, result);
    this.getAttachmentDetails(attachmentsData, result);

    result.carrierName = this.getClientDescription(order.customerId, vocabData);
    const sortedProviders = [...result.providers].sort((a, b) => a[0].localeCompare(b[0], undefined, { numeric: true, sensitivity: 'base' }));
    const sortedProvidersMap = new Map(sortedProviders);
    result.providers = sortedProvidersMap;

    return result;
  }

  // formatDate(date?: string): string {
  //   return this.datePipe.transform(date, 'MM/dd/yyyy', 'UTC') || '';
  // }

  formatDate(date?: string): string {
    // return this.datePipe.transform(date, 'MM/dd/yyyy', 'UTC') || '';

    if (!date) return '';

    let parsedDate: Date;

    // Check if date includes time or timezone info
    const hasTimeOrZone = /T|\d{2}:\d{2}/.test(date);

    if (!hasTimeOrZone) {
      parsedDate = new Date(date + 'T00:00:00Z');
    } else {
      parsedDate = new Date(date);
    }

    return this.datePipe.transform(parsedDate, 'MM/dd/yyyy') || '';
  }

  getOrderTypeDescription(orderType: string = '', vocabData: any) {
    const vocabOrderData = vocabData?.find((d: any) => d.vocabularyName === 'OrderType');
    const selectedType = vocabOrderData?.vocabularyItems?.find((item: any) => item?.code === orderType);
    return selectedType?.description;
  }

  getTitleDescription(title: string = '', vocabData: any) {
    const vocabTitleData = vocabData?.find((d: any) => d.vocabularyName === 'ContactTitle');
    const selectedTitle = vocabTitleData?.vocabularyItems?.find((item: any) => item?.code === title);
    return selectedTitle?.description;
  }

  getStateDescription(city: string = '', vocabData: any) {
    const vocabCityData = vocabData?.find((d: any) => d.vocabularyName === 'PhysicalLocationUsAddressStateCode');
    const selectedCity = vocabCityData?.vocabularyItems?.find((item: any) => item?.code === city);
    return selectedCity?.description;
  }

  getClientDescription(clientCode: string = '', vocabData: any) {
    const vocabCityData = vocabData?.find((d: any) => d.vocabularyName === 'RecordRetrievalCustomerCode');
    const selectedCity = vocabCityData?.vocabularyItems?.find((item: any) => item?.code === clientCode);
    return selectedCity?.description;
  }

  getContactDetails(parties: any, result: OrderDetails, caseData:any) {
    const defendantParty = parties.find((partyObj:any) =>
      partyObj.roles?.some((role:any) => role.roleCode === 'defendant-attorney')
    );

    if (defendantParty) {
      this.addDefendentDataToDisplay(defendantParty, result, caseData);
    }
    parties?.forEach((element: any) => {
      const distinctRoleCodes = _.uniq(_.map(element.roles, 'roleCode'));

      if (distinctRoleCodes.length === 1) {
        if (distinctRoleCodes[0] === 'defendant-attorney' || distinctRoleCodes[0] === 'defendant-paralegal' || distinctRoleCodes[0] === 'contact-other') {
          this.addContactToDisplay(element, result);
        }
        if (distinctRoleCodes[0] === 'co-defendant-attorney') {
          this.addDefendentDataToDisplay(element, result,caseData);
        }
        if (distinctRoleCodes[0] === 'plaintiff-attorney') {
          this.addCounselInfoToDisplay(element, result,caseData);
        }
        if (distinctRoleCodes[0] === 'court') {
          this.addCourtDetailsToDisplay(element, result);
        }
      }
    });
  }

  addContactToDisplay(element: any, result: OrderDetails) {
    let displayName: string = element.party.displayName;
    let email: string = '';
    let phone!: number;
    let extension!: string;
    let title: string = '';
    let barNumber: string = '';

    element.party.locators.forEach((locator: any) => {
      if (locator.email) {
        email = locator.email.address;
      }
      if (locator.phone) {
        phone = locator.phone.number;
        extension = locator.phone.extension;
      }
    });

    element.party.names.forEach((names: any) => {
      if (_.toLower(names.function) === 'title') {
        title = this.getTitleDescription(names.text, this.vocabData);
        // title = names.text;
      }
    });

    element.roles.forEach((role: any) => {
      barNumber = role?.attorney?.barNumber;
    })

    const newContact = {
      displayName: displayName,
      phone: phone,
      extension: extension,
      email: email,
      title: title,
      barNumber: barNumber
    };
    result.contacts.push(newContact);
  }

  getRetrievalDetails(retrievalData: any, result: OrderDetails) {
    result.specialInstructions = retrievalData.specialInstructions;

    retrievalData.parties.forEach((element: any) => {
      if (element.roles.some((role: any) => role.roleCode === 'bill-to')) {
        this.addBillingDetailsToDisplay(element, result,);
      }
    });

    retrievalData.parties.forEach((element: any) => {
      if (element.roles.some((role: any) => role.roleCode === 'adjuster')) {
        this.addAdjusterDetailsToDisplay(element, result);
      }
    });

    retrievalData.parties.forEach((element: any) => {
      if (element.roles.some((role: any) => role.roleCode === 'deliver-to')) {
        this.addDeliveryDetailsToDisplay(element, result);
      }
    });

    retrievalData.parties.forEach((element: any) => {
      if (element.roles.some((role: any) => role.roleCode === 'record-subject')) {
        this.addRecordSubjectDetailsToDisplay(element, result);
      }
    });

    retrievalData.parties.forEach((element: any) => {
      if (element.roles.some((role: any) => role.roleCode === 'records-custodian')) {
        this.addProviderInfoToDisplay(element, retrievalData, result, this.vocabData);
      }
    });

    retrievalData.parties.forEach((element: any) => {
      if (element.roles.some((role: any) => role.roleCode === 'medical-provider')) {
        this.addMedicalProviderDetailsToDisplay(element, result);
      }
    });
  }

  addBillingDetailsToDisplay(element: any, result: OrderDetails) {
    let email: string = '';
    let billToText: string = '';

    element.party.locators.forEach((locator: any) => {
      if (locator.email) {
        email = locator.email.address;
      }
    });
    element.party.names.forEach((names: any) => {
      if (_.toLower(names.function) === 'business-name') {
        billToText = names.text
      }
    });
    const billDetail = {
      email: email,
      billTo: billToText
    };
    result.billingInfo = billDetail;
  }

  addAdjusterDetailsToDisplay(element: any, result: OrderDetails) {
    let email: string = '';
    let adjusterName: string = element.party.displayName;

    element.party.locators.forEach((locator: any) => {
      if (locator.email) {
        email = locator.email.address;
      }
    });

    const adjusterDetail = {
      email: email,
      adjusterName: adjusterName,
    };
    result.adjusterInfo = adjusterDetail;
  }

  addDeliveryDetailsToDisplay(element: any, result: OrderDetails) {
    let attentionTo: string = '';
    let firmTo: string = element.party.displayName;
    let phone: string = '';
    let extension: string = '';
    let address: string = '';

    element.party.names.forEach((names: any) => {
      if (_.toLower(names.function) === 'contact-name') {
        attentionTo = names.text;
      }
    });

    element.party.locators.forEach((locator: any) => {
      if (locator.phone) {
        locator.phone.purpose === 'work' ? phone = locator.phone.number : '';
        locator.phone.purpose === 'work' ? extension = locator.phone.extension : '';
      }
      if (locator.location) {
        address = (locator.location.location.usAddress.lineOne ? locator.location.location.usAddress.lineOne + ', ' : '')
          + (locator.location.location.usAddress.city ? locator.location.location.usAddress.city + ', ' : '')
          + (locator.location.location.usAddress.stateCode ? this.getStateDescription(locator.location.location.usAddress.stateCode, this.vocabData) + ', ' : '')
          + (locator.location.location.usAddress.postalCode ? locator.location.location.usAddress.postalCode : '');
      }
    });

    const deliveryDetail = {
      attentionTo: attentionTo,
      firmTo: firmTo,
      phone: phone,
      extension: extension,
      address: address
    };

    result.deliveryInfo = deliveryDetail;
  }

  getAttachmentDetails(attachmentsData: any, result: OrderDetails) {
    let storageId: string = '';
    let storageProviderId: string = '';
    let contentType: string = '';
    let fileName: string = '';
    let displayName: string = '';
    let description: string = '';
    let sizeBytes!: number;
    let sizeText: string = '';

    attachmentsData.forEach((element: any) => {
      if (element.document) {
        storageId = element.document.storageId;
        storageProviderId = element.document.storageProviderId;
        contentType = element.document.contentType;
        fileName = element.document.fileName;
        description = element.document.description;
        sizeBytes = element.document.sizeBytes;
        sizeText = element.document.sizeText;
        displayName = element.document.displayName;
      }

      const attachmentDetail = {
        documentId: storageId,
        documentListId: storageProviderId,
        // contentType: contentType,
        fileName: fileName,
        // displayName: displayName,
        description: description,
        // sizeBytes: sizeBytes,
        // sizeText: sizeText
      };
      result.attachmentInfo.push(attachmentDetail as DocumentReference);
    });
  }

  addRecordSubjectDetailsToDisplay(element: any, result: OrderDetails) {
    element.roles.forEach((role: any) => {
      if (role.roleCode === 'record-subject') {
        let name: string = element.party.displayName;
        let alias: string = '';
        let dateOfBirth: string = '';
        let lastDigitOfSSN: string = '';
        let dateOfDeath: string = '';

        element.party.names.forEach((names: any) => {
          if (_.toLower(names.function) === 'alias') {
            alias = names.text;
          }
        });
        dateOfBirth = this.dateUtilService.formatCalendarDateForDisplay(element?.party?.person?.dateOfBirth);
        lastDigitOfSSN = role.recordSubject?.socialSecurityNumber?.slice(-4);

        dateOfDeath = this.dateUtilService.formatCalendarDateForDisplay(element?.party?.person?.dateOfDeath);

        let requestNumbers = role.requestNumbers;

        const recordSubject = {
          name: name,
          alias: alias,
          dateOfBirth: dateOfBirth,
          lastDigitOfSSN: lastDigitOfSSN,
          dateOfDeath: dateOfDeath,
          requestNumbers: requestNumbers
        };

        result.subjects.push(recordSubject);
      }
    });
  }

  addDefendentDataToDisplay(element: any, result: OrderDetails, caseData:any) {
    let defendants: Defendant[] = [];
    let defendantCounselAttorneyName: string = '';
    let defendantCounselFirmName: string = '';
    let defendantCounselBarNumer: string = '';
    let streetAddress: string = '';
    let city: string = '';
    let state: State = { name: '', value: '' };
    let zip: string = '';
    let phone: string = '';
    let extension: string = '';
    let fax: string = '';
    let email: string = '';
    let keys: any = element?.party?.keys;
    let recordSubjectName: RecordSubjectName[] = [];
    

    caseData?.partyRelationships.forEach((relation: any) => {
      if (
        relation?.sourcePartyKey ===
        keys?.find((item: any) => item?.typeCode === 'external-id')?.components['external-id']
      ) {
        
        caseData?.parties.forEach((party: any) => {
          if (party?.party?.keys?.find((item: any) => item?.typeCode === 'external-id')?.components['external-id'] === relation?.targetPartyKey) {
            let defendant: Defendant = {
              name: party?.party?.displayName,
              keys: party?.party?.keys,
              streetAddress: '',
              city: '',
              state: { name: '', value: '' },
              value: party?.party?.displayName,
              zip: ''
            };
            defendants.push(defendant);
          }
        
      });
    }
    });
    if (element.roles.length > 0) {
      element.roles.forEach((item: any) => {
        defendantCounselBarNumer = item?.attorney?.barNumber || '';
      });
    }
    element.party.names.forEach((names: any) => {
      if (_.toLower(names.function) === 'business-name') {
        defendantCounselFirmName = names.text;
      }
      else if (_.toLower(names.function) === 'contact-name') {
        defendantCounselAttorneyName = names.text;
      }
    });

    element.party.locators.forEach((locator: any) => {
      if (locator.typeCode === 'email') {
        email = locator.email?.address;
      }
      else if (locator.phone) {
        if (locator.phone.purpose === 'work') {
          phone = locator.phone?.number;
          extension = locator.phone?.extension;
        }
        if (locator.phone.purpose === 'fax') {
          fax = locator.phone?.number;
        }
      }
      else if (locator.typeCode === 'location') {
        streetAddress = locator?.location?.location?.usAddress?.lineOne;
        city = locator?.location?.location?.usAddress?.city;
        state.value = locator?.location?.location?.usAddress?.stateCode;
        state.name = this.getStateDescription(state.value, this.vocabData);
        zip = locator?.location?.location?.usAddress?.postalCode;
      }
    });

    const counselInfo: DefendantCounsel = {
      defendants: defendants,
      defendantCounselAttorneyName: defendantCounselAttorneyName,
      defendantCounselFirmName: defendantCounselFirmName,
      streetAddress: streetAddress,
      city: city,
      state: state,
      zip: zip,
      phone: phone,
      extn: extension,
      fax: fax,
      email: email,
      keys: element?.party?.keys,
      defendantCounselBarNumer: defendantCounselBarNumer
    };

    if (result.subpoena?.defendant) {
      result.subpoena.defendant.push(counselInfo);
    }
  }

  addCounselInfoToDisplay(element: any, result: OrderDetails, caseData:any) {
    let plaintiffs: Plaintiff[] = [];
    let plantiffCounselAttorneyName: string = '';
    let plantiffCounselFirmName: string = '';
    let plantiffCounselBarNumer: string = '';
    let streetAddress: string = '';
    let city: string = '';
    let state: State = { name: '', value: '' };
    let zip: string = '';
    let phone: string = '';
    let extension: string = '';
    let fax: string = '';
    let email: string = '';
    let keys: any = element?.party?.keys;
    let recordSubjectName: RecordSubjectName[] = [];

    caseData?.partyRelationships.forEach((relation: any) => {
          if (
            relation?.sourcePartyKey ===
            keys?.find((item: any) => item?.typeCode === 'external-id')?.components['external-id']
          ) {
            
            caseData?.parties.forEach((party: any) => {
              if (party?.party?.keys?.find((item: any) => item?.typeCode === 'external-id')?.components['external-id'] === relation?.targetPartyKey) {
                let plaintiff: Plaintiff = {
                  name: party?.party?.displayName,
                  keys: party?.party?.keys,
                  streetAddress: '',
                  city: '',
                  state: { name: '', value: '' },
                  value: party?.party?.displayName,
                  zip: ''
                };
                plaintiffs.push(plaintiff);
              }
            
          });
        }
        });
    
    if (element.roles.length > 0) {
      element.roles.forEach((item: any) => {
        plantiffCounselBarNumer = item?.attorney?.barNumber || '';
      });
    }
    element.party.names.forEach((names: any) => {
      if (_.toLower(names.function) === 'business-name') {
        plantiffCounselFirmName = names.text;
      }
      else if (_.toLower(names.function) === 'contact-name') {
        plantiffCounselAttorneyName = names.text;
      }
    });

    element.party.locators.forEach((locator: any) => {
      if (locator.typeCode === 'email') {
        email = locator.email?.address;
      }
      else if (locator.phone) {
        if (locator.phone.purpose === 'work') {
          phone = locator.phone?.number;
          extension = locator.phone?.extension;
        }
        if (locator.phone.purpose === 'fax') {
          fax = locator.phone?.number;
        }
      }
      else if (locator.typeCode === 'location') {
        streetAddress = locator?.location?.location?.usAddress?.lineOne;
        city = locator?.location?.location?.usAddress?.city;
        state.value = locator?.location?.location?.usAddress?.stateCode;
        state.name = this.getStateDescription(state.value, this.vocabData);
        zip = locator?.location?.location?.usAddress?.postalCode;
      }
    });
  
    const counselInfo: PlaintiffCounsel = {
      plaintiffs: plaintiffs,
      plantiffCounselAttorneyName: plantiffCounselAttorneyName,
      plantiffCounselFirmName: plantiffCounselFirmName,
      streetAddress: streetAddress,
      city: city,
      state: state,
      zip: zip,
      phone: phone,
      extn: extension,
      fax: fax,
      email: email,
      keys: element?.party?.keys,
      plantiffCounselBarNumer: plantiffCounselBarNumer
    };

    if (result.subpoena && !result.subpoena.counselInfo)
      result.subpoena.counselInfo = [];
    result.subpoena.counselInfo.push(counselInfo);
    result.counselInfo.push(counselInfo);
      
  }

  addCourtDetailsToDisplay(element: any, result: OrderDetails) {
    let judge: string = '';
    let court: string = element.party.displayName;
    let courtAddress: string = '';
    let jurisdictionCounty: string = '';
    let state: string = '';

    element.roles.forEach((role: any) => {
      judge = role.court?.judgeName;
      jurisdictionCounty = role.court?.jurisdiction;
    });

    element.party.locators.forEach((locator: any) => {
      if (locator.typeCode === 'location') {
        courtAddress = (locator.location.location.usAddress.lineOne ? locator.location.location.usAddress.lineOne + ', ' : '')
          + (locator.location.location.usAddress.city ? locator.location.location.usAddress.city + ', ' : '')
          + (locator.location.location.usAddress.stateCode ? this.getStateDescription(locator.location.location.usAddress.stateCode, this.vocabData) + ', ' : '')
          + (locator.location.location.usAddress.postalCode ? locator.location.location.usAddress.postalCode : '');
      }
    });

    result.subpoena.judge = judge;
    result.subpoena.court = court;
    result.subpoena.courtAddress = courtAddress;
    result.subpoena.jurisdictionCounty = jurisdictionCounty;
    result.subpoena.state = state;
  }

  addProviderInfoToDisplay(element: any, retrievalData: any, result: OrderDetails, vocabData: any) {
    let providerInfo: any = {};
    let requestNumber: number;
    let provider: string = element.party.displayName;
    let address: string = '';
    let email: string = '';
    let phone!: string;
    let extension!: string;
    let fax: string = '';
    let recordType: string = '';
    let recordSubject: string = '';
    let doctor: string = '';
    let subpoenaLanguage: string = '';
    let recordRange: string = '';
    let notes: string = '';
    let ambulanceDateOfService: string = '';
    let ambulanceTimeOfService: string = '';
    let ambulancePickupAddress: string = '';
    let ambulanceDropAddress: string = '';
    let militaryBranch: string = '';
    let militaryServiceNumber: string = '';
    let va: string = '';
    let unionMembershipNumber: string = '';
    let otherDescriptionOfRecords: string = '';
    let diagnosticTypeCodes: string = '';
    let diagnosticComments: string = '';
    let diagnosticFocusArea: string = '';
    let insuranceClaimNumber: string = '';
    let insurancePolicyNumber: string = '';
    let employmentSSN: string = '';
    let irsSSN: string = '';
    let socialSecuritySSN: string = '';
    let ssnAddress: string = '';

    element.roles.forEach((role: any) => {
      if (role.roleCode === 'records-custodian' && role.requestNumbers) {
        role.requestNumbers.forEach((requestNumber: any) => {
          recordType = '';
          element.party.locators.forEach((locator: any) => {
            if (locator.typeCode === 'email') {
              email = locator.email?.address;
            }

            if (locator.typeCode === 'phone' && locator.phone.purpose === 'work') {
              phone = locator.phone?.number;
              extension = locator.phone?.extension;
            }
            if (locator.typeCode === 'phone' && locator.phone.purpose === 'fax') {
              fax = locator.phone?.number;
            }

            if (locator.typeCode === 'location') {
              address = (locator.location.location.usAddress.lineOne ? locator.location.location.usAddress.lineOne + ', ' : '')
                + (locator.location.location.usAddress.lineTwo ? locator.location.location.usAddress.lineTwo + ', ' : '')
                + (locator.location.location.usAddress.city ? locator.location.location.usAddress.city + ', ' : '')
                + (locator.location.location.usAddress.stateCode ? this.getStateDescription(locator.location.location.usAddress.stateCode, this.vocabData) + ', ' : '')
                + (locator.location.location.usAddress.postalCode ? locator.location.location.usAddress.postalCode : '');
            }
          });

          recordSubject = result.subjects.find((subject: any) => subject.requestNumbers?.includes(requestNumber))?.name ?? '';

          retrievalData.requests.forEach((request: any) => {
            if (request.requestNumber === requestNumber) {
              request.scope.forEach((scope: any) => {
                recordRange = (scope.beginDate && scope.endDate) ? (this.formatDate(scope.beginDate) + ' - ' + this.formatDate(scope.endDate)) : 'Any or All';
                const vocabRecordTypeData = vocabData?.find((d: any) => d.vocabularyName === 'RecordTypes');
                if (recordType === '') {
                  const recordTypeData = vocabRecordTypeData?.vocabularyItems?.find((item: any) => item?.code === scope.recordTypeCode);
                  recordType = recordTypeData?.description;
                }
                else {
                  const recordTypeData = vocabRecordTypeData?.vocabularyItems?.find((item: any) => item?.code === scope.recordTypeCode);
                  recordType = recordType + ', ' + recordTypeData?.description;
                }

                if (scope.recordTypeCode === 'ambulance') {
                  ambulanceDateOfService = scope.qualifiers.ambulanceDateOfService ? scope.qualifiers.ambulanceDateOfService : '';
                  ambulanceTimeOfService = scope.qualifiers.ambulanceTimeOfService ? scope.qualifiers.ambulanceTimeOfService : '';
                  ambulancePickupAddress = (scope.qualifiers.ambulancePickupLine1 ? scope.qualifiers.ambulancePickupLine1: '')
                        + (scope.qualifiers.ambulancePickupCity ? ', ' + scope.qualifiers.ambulancePickupCity : '')
                        + (scope.qualifiers.ambulancePickupState ? ', ' + this.getStateDescription(scope.qualifiers.ambulancePickupState, this.vocabData) : '')
                        + (scope.qualifiers.ambulancePickupPostal ? ', ' + scope.qualifiers.ambulancePickupPostal : '');
                  ambulanceDropAddress = (scope.qualifiers.ambulanceDropOffLine1 ? scope.qualifiers.ambulanceDropOffLine1 : '')
                        + (scope.qualifiers.ambulanceDropOffCity ? ', ' + scope.qualifiers.ambulanceDropOffCity : '')
                        + (scope.qualifiers.ambulanceDropOffState ? ', ' + this.getStateDescription(scope.qualifiers.ambulanceDropOffState, this.vocabData) : '')
                        + (scope.qualifiers.ambulanceDropOffPostal ? ', ' + scope.qualifiers.ambulanceDropOffPostal : '');
                }
                if (scope.recordTypeCode === 'military') {
                  militaryBranch = scope.qualifiers.militaryBranch;
                  militaryServiceNumber = scope.qualifiers.militaryServiceNumber;
                }
                if (scope.recordTypeCode === 'va') {
                  const vaData = vocabData?.find((d: any) => d.vocabularyName === 'VA_Options');
                  let vaMedicalRecords: string = '';
                  let vaBenefitRecords: string = '';
                  vaData.vocabularyItems.forEach((item: any) => {
                    if (item.code === scope.qualifiers.vaMedicalRecords[0] || item.code === scope.qualifiers.vaMedicalRecords) {
                      vaMedicalRecords = item.description;
                    }
                    if (item.code === scope.qualifiers.vaBenefitRecords[0] || item.code === scope.qualifiers.vaBenefitRecords) {
                      vaBenefitRecords = item.description;
                    }
                  });

                  va = (vaMedicalRecords ? vaMedicalRecords : '')
                    + (vaMedicalRecords && vaBenefitRecords ? ', ' + vaBenefitRecords : vaBenefitRecords ? vaBenefitRecords : '');
                }
                if (scope.recordTypeCode === 'union') {
                  unionMembershipNumber = scope.qualifiers?.unionMembershipNumber || '--';
                }
                if (scope.recordTypeCode === 'other') {
                  otherDescriptionOfRecords = scope.qualifiers?.otherDescriptionOfRecords || '--';
                }
                if (scope.recordTypeCode === 'diagnostic-images') {
                  diagnosticTypeCodes = scope.qualifiers?.diagnosticTypeCodes;
                  diagnosticFocusArea = scope.qualifiers?.diagnosticFocusArea;
                  diagnosticComments = scope.qualifiers?.diagnosticComments;
                }
                if (scope.recordTypeCode === 'insurance') {
                  insuranceClaimNumber = scope.qualifiers?.insuranceClaimNumber;
                  insurancePolicyNumber = scope.qualifiers?.insurancePolicyNumber;
                }
                if (scope.recordTypeCode === 'employment') {
                  employmentSSN = this.mapSSNforRecord(retrievalData, requestNumber);
                }
                if (scope.recordTypeCode === 'irs') {
                  irsSSN = this.mapSSNforRecord(retrievalData, requestNumber);
                }
                if (scope.recordTypeCode === 'social-security') {
                  socialSecuritySSN = this.mapSSNforRecord(retrievalData, requestNumber);
                  ssnAddress = this.mapRecordAddress(retrievalData, requestNumber);
                }
              });

              subpoenaLanguage = request.subpoenaLanguage;
              notes = request.notes;

            } // if request number matches scope
          });

          providerInfo = {
            provider: provider,
            address: address,
            email: email,
            phone: phone,
            extn: extension,
            fax: fax,
            recordSubject: recordSubject,
            recordRange: recordRange,
            recordType: recordType,
            subpoenaLanguage: subpoenaLanguage,
            notes: notes,
            doctor: '',
            ambulance: {
              date: this.formatDate(ambulanceDateOfService),
              time: ambulanceTimeOfService,
              pickupAddress: ambulancePickupAddress,
              dropAddress: ambulanceDropAddress
            },
            imaging: {
              type: diagnosticTypeCodes,
              others: diagnosticComments,
              specificBodyPart: diagnosticFocusArea
            },
            employment: {
              ss: employmentSSN
            },
            insurance: {
              claimNumber: insuranceClaimNumber,
              policyNumber: insurancePolicyNumber
            },
            irs: {
              ss: irsSSN
            },
            military: {
              branch: militaryBranch,
              serviceNumber: militaryServiceNumber
            },
            socialSecurity: {
              ss: socialSecuritySSN,
              address: ssnAddress
            },
            union: {
              membershipNumber: unionMembershipNumber
            },
            va: {
              va: va
            },
            other: {
              descriptionOfRecords: otherDescriptionOfRecords
            }
          };

          result.providers.set(requestNumber, providerInfo);
  
        }); // for requests
      } // if
    }); // for roles
  }

  addMedicalProviderDetailsToDisplay(element: any, result: OrderDetails) {
    let doctor: string = element.party.displayName;

    element?.roles?.forEach((role: any) => {
      role?.requestNumbers?.forEach((requestNumber: any) => {
        let provider = result?.providers?.get(requestNumber);
        if (provider) {
          provider.doctor = doctor;
        }
      });
    });
  }

  getSubject(orderDetails: RootObject) {
    return orderDetails.retrievalObjective.parties?.find(p => p.roles?.some(r => r.requestNumbers));
  }

  mapSSNforRecord(retrievalData: any, requestNumber: number) {
    let ssn: string = '';
    retrievalData.parties.forEach((party: any) => {
      party.roles.forEach((role: any) => {
        if (role.roleCode === 'record-subject' && role.requestNumbers?.includes(requestNumber)) {
          ssn = role.recordSubject.socialSecurityNumber;
        }
      });
    });
    return ssn;
  }

  mapRecordAddress(retrievalData: any, requestNumber: number) {
    let ssnAddress: string = '';
    retrievalData.parties.forEach((partys: any) => {
      if ((partys.party.locators.length > 0) && partys.party.locators[0].location && partys.roles[0].requestNumbers?.includes(requestNumber)) {
        ssnAddress = (partys.party.locators[0].location.location.usAddress.lineOne ? partys.party.locators[0].location.location.usAddress.lineOne + ', ' : '')
          + (partys.party.locators[0].location.location.usAddress.city ? partys.party.locators[0].location.location.usAddress.city + ', ' : '')
          + (partys.party.locators[0].location.location.usAddress.stateCode ? this.getStateDescription(partys.party.locators[0].location.location.usAddress.stateCode, this.vocabData) + ', ' : '')
          + (partys.party.locators[0].location.location.usAddress.postalCode ? partys.party.locators[0].location.location.usAddress.postalCode : '');
      }
    });
    return ssnAddress;
  }

  getSelectedTenant(result:OrderDetails) {
    this.tenantKeyService.getTenantKey().subscribe((selectedTenant) => {
      result.carrierName = selectedTenant ?? '';
    });
  }

  getAccountNameDescription(organizationValue: string = '', vocabData: any) {
    let orgVocabData = vocabData?.find((d: any) => d?.vocabularyName === 'UserOrganization');
    let selectedOrg = orgVocabData?.vocabularyItems?.find((item: any) => item?.code === organizationValue );
    return selectedOrg?.description ?? organizationValue;
  }

  getDisplayName(username: string = '', users: any): string {
    const user = users?.find((user: any) => user?.principalName === username);
    return user?.displayName || '';
  }

  getOrderStatusDescription(state: string = '', vocabData: any) {
    const selectedStatus =
      vocabData?.find((d: any) => d.vocabularyName === 'OrderStatusCode')
        ?.vocabularyItems?.find((item: any) => item?.code === state)
      ||
      vocabData?.find((d: any) => d.vocabularyName === 'RecordRetrievalRequestStatusCode')
        ?.vocabularyItems?.find((item: any) => item?.code === state);
    return selectedStatus?.description;
  }
}
